import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState, useCallback, useEffect } from "react";

import type { DragonTileSelection } from "@/components/DragonTilePicker/types";
import type { Frame } from "@/types/dragon-tiles";

import { useDuplicateFrame } from "@/api/dragon-tiles";
import { DragonTilePicker } from "@/components/DragonTilePicker/DragonTilePicker";
import dayjs from "@/utils/configuredDayjs";

interface DuplicateFrameModalProps {
  open: boolean;
  onClose: () => void;
  frame: Frame;
  siteId: string;
  onSuccess?: () => void;
}

export function DuplicateFrameModal({
  open,
  onClose,
  frame,
  siteId,
  onSuccess,
}: DuplicateFrameModalProps) {
  const [frameName, setFrameName] = useState("");
  const [pickerKey, setPickerKey] = useState(0);
  const [selection, setSelection] = useState<DragonTileSelection>({
    dragonTileId: "",
    innerTileId: undefined,
    hasError: false,
  });
  const duplicateFrame = useDuplicateFrame();

  const effectiveInnerTileId = selection.innerTileId;

  // Initialize frame name when modal opens
  useEffect(() => {
    if (open) {
      const timestamp = dayjs().format("MM/DD/YYYY h:mm A");
      setFrameName(`${timestamp} - ${frame.name}`);
    }
  }, [open, frame.name]);

  const handleSelectionChange = useCallback(
    (newSelection: DragonTileSelection) => {
      setSelection(newSelection);
    },
    [],
  );

  const handleClose = () => {
    onClose();
    // Reset state after close
    setFrameName("");
    setPickerKey((k) => k + 1);
    setSelection({
      dragonTileId: "",
      innerTileId: undefined,
      hasError: false,
    });
  };

  const handleDuplicate = async () => {
    if (!effectiveInnerTileId) return;

    try {
      const timestamp = dayjs().format("MM/DD/YYYY h:mm A");

      await duplicateFrame.mutateAsync(
        {
          siteId,
          frameId: frame.id,
          payload: {
            name: frameName.trim() || `${timestamp} - ${frame.name}`,
            inner_tile_id: effectiveInnerTileId,
          },
        },
        {
          onSuccess: () => {
            enqueueSnackbar("Tile duplicated successfully!", {
              variant: "success",
            });
            handleClose();
            onSuccess?.();
          },
        },
      );
    } catch (error) {
      console.error("Failed to duplicate tile", error);
      enqueueSnackbar("Failed to duplicate tile. Please try again.", {
        variant: "error",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Tile</DialogTitle>
      <DialogContent>
        <Stack spacing={4} sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body2">Tile Name</Typography>
            <TextField
              autoFocus
              type="text"
              fullWidth
              value={frameName}
              onChange={(e) => setFrameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleDuplicate();
                }
              }}
            />
          </Stack>

          <DragonTilePicker
            key={pickerKey}
            siteId={siteId}
            enabled={open}
            defaultInnerTileId={frame.inner_tile}
            onSelectionChange={handleSelectionChange}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={duplicateFrame.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleDuplicate()}
          variant="contained"
          disabled={
            duplicateFrame.isPending ||
            !frameName.trim() ||
            frameName.trim() === frame.name.trim() ||
            !selection.dragonTileId ||
            selection.hasError
          }
        >
          {duplicateFrame.isPending ? "Duplicating..." : "Duplicate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
